// SaveOrganicViewModal.jsx

import React, { useEffect, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';

/**
 * SaveOrganicViewModal
 *
 * Minimal title prompt for saving an organic catch-all draft as a Saved View.
 *
 * @param {boolean} isOpen
 * @param {function} setOpen
 * @param {string} [defaultTitle]
 * @param {boolean} [saving=false]
 * @param {function} onSave - Called with the trimmed title.
 * @returns {ReactNode}
 */
const SaveOrganicViewModal = React.memo(({
    isOpen,
    setOpen,
    defaultTitle = '',
    saving = false,
    onSave,
}) => {

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- STATE ----- //

    const [title, setTitle] = useState(defaultTitle);

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!isOpen) return;
        setTitle(defaultTitle);
    }, [defaultTitle, isOpen]);

    // ----- HANDLERS ----- //

    /**
     * handleSave
     *
     * Submits the trimmed title when valid.
     */
    const handleSave = () => {
        const nextTitle = String(title || '').trim();
        if (!nextTitle || saving) return;
        onSave?.(nextTitle);
    };

    const canSave = Boolean(String(title || '').trim()) && !saving;

    // ----- RENDER ----- //

    return (
        <Modal
            title="Save View"
            isOpen={isOpen}
            height="auto"
            width={isMobile ? '100%' : '420px'}
            onClose={() => setOpen(false)}
        >
            <ModalForm>
                <ModalField>
                    <label
                        htmlFor="organic-view-title"
                        style={{
                            display: 'block',
                            fontSize: 13,
                            marginBottom: 8,
                            opacity: 0.8,
                        }}
                    >
                        Title
                    </label>
                    <input
                        id="organic-view-title"
                        value={title}
                        autoFocus
                        disabled={saving}
                        placeholder="View title"
                        style={{
                            background: 'var(--backgroundMuted)',
                            border: '1px solid var(--border)',
                            borderRadius: 8,
                            color: 'var(--foreground)',
                            fontSize: 14,
                            outline: 'none',
                            padding: '10px 12px',
                            width: '100%',
                        }}
                        onChange={(event) => setTitle(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                handleSave();
                            }
                        }}
                    />
                </ModalField>
            </ModalForm>

            <ModalButtons>
                <ModalButtonSection align="END">
                    <Button
                        label="Cancel"
                        enabled={!saving}
                        onClick={() => setOpen(false)}
                    />
                    <Button
                        label={saving ? 'Saving…' : 'Save'}
                        enabled={canSave}
                        onClick={handleSave}
                    />
                </ModalButtonSection>
            </ModalButtons>
        </Modal>
    );
});

// Dev Tools
SaveOrganicViewModal.displayName = 'SaveOrganicViewModal';

export default SaveOrganicViewModal;
